import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, 
  Button, 
  Alert, 
  Card, 
  Tabs, 
  Tab,
  Form,
  Spinner,
  Row,
  Col,
  Badge
} from 'react-bootstrap';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Area,
  AreaChart,
  ScatterChart,
  Scatter,
  Cell
} from 'recharts';
import {
  getCirclesByOrgId,
  getCirclePercentileData,
  getGradePercentileData,
  getCurrentOrgId,
  ORG_ID_CHANGE_EVENT,
  analyzeCircleWithGradingSystem,
  getCircles,
  getGradingSystems,
  getJobStatus,
  getJobResult,
  getOrganizationJobs,
  getGradingSystemsByOrgId
} from '../services/api';

// Theme colors
const THEME = {
  primary: '#00A651',
  primaryDark: '#008c44',
  primaryDarker: '#006633',
  primaryLight: '#e8f5e9',
  primaryLighter: '#f1f8e9',
  grey100: '#f8f9fa',
  grey200: '#e9ecef',
  grey300: '#dee2e6',
  grey700: '#495057',
  grey800: '#343a40',
  grey900: '#212529',
  success: '#00A651',
  danger: '#dc3545',
  chart: {
    colors: [
      '#00A651', // primary green
      '#4caf50', // lighter green
      '#2e7d32', // darker green
      '#81c784', // pale green
      '#388e3c', // forest green
      '#66bb6a'  // medium green
    ]
  }
};

const customStyles = `
  .custom-form-check .form-check-input:checked {
    background-color: ${THEME.primary};
    border-color: ${THEME.primary};
  }
`;

const DataAnalysis = ({ onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedCircles, setSelectedCircles] = useState([]);
  const [availableCircles, setAvailableCircles] = useState([]);
  const [orgId, setOrgId] = useState(() => {
    const currentOrgId = getCurrentOrgId();
    // Ensure we have a valid orgId, default to '169' if null or invalid
    return currentOrgId && currentOrgId !== 'null' && currentOrgId !== 'undefined' ? currentOrgId : '169';
  });

  useEffect(() => {
    const fetchCircles = async () => {
      // Validate orgId before making API call
      if (!orgId || orgId === 'null' || orgId === 'undefined') {
        console.warn('Invalid orgId:', orgId);
        setError('Please select a valid organization');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await getCirclesByOrgId(orgId);
        
        // Handle both wrapped responses (with .result) and direct array responses
        if (response?.successful && response?.result) {
          const formattedCircles = response.result.map(circle => ({
            id: Number(circle.id),
            name: circle.circleName
          }));
          setAvailableCircles(formattedCircles);
        } else if (Array.isArray(response)) {
          // Handle direct array response
          const formattedCircles = response.map(circle => ({
            id: Number(circle.id),
            name: circle.circleName
          }));
          setAvailableCircles(formattedCircles);
        } else if (response?.result && Array.isArray(response.result)) {
          // Handle response with result array
          const formattedCircles = response.result.map(circle => ({
            id: Number(circle.id),
            name: circle.circleName
          }));
          setAvailableCircles(formattedCircles);
        } else {
          throw new Error(response?.errors || 'Failed to fetch circles');
        }
      } catch (err) {
        console.error('Error fetching circles:', err);
        setError('Failed to fetch available circles');
      } finally {
        setLoading(false);
      }
    };

    fetchCircles();
  }, [orgId]);

  // Listen for orgId changes
  useEffect(() => {
    const handleOrgIdChange = (event) => {
      const detail = event.detail;
      const newOrgId = typeof detail === 'object' ? (detail.orgId || getCurrentOrgId()) : detail || getCurrentOrgId();
      // Validate and sanitize orgId
      const validOrgId = newOrgId && newOrgId !== 'null' && newOrgId !== 'undefined' ? newOrgId : '169';
      if (validOrgId !== orgId) {
        setOrgId(validOrgId);
      }
    };

    window.addEventListener(ORG_ID_CHANGE_EVENT, handleOrgIdChange);
    return () => {
      window.removeEventListener(ORG_ID_CHANGE_EVENT, handleOrgIdChange);
    };
  }, [orgId]);

  const handleCircleSelect = (e) => {
    const circleId = e.target.value;
    if (circleId && !selectedCircles.includes(circleId)) {
      setSelectedCircles([...selectedCircles, circleId]);
    }
  };

  const handleRemoveCircle = (circleId) => {
    setSelectedCircles(selectedCircles.filter(id => id !== circleId));
  };

  const handleCirclePercentileAnalysis = async () => {
    if (!selectedCircles.length) {
      setError('Please select at least one circle');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setData(null);
      
      console.log('Analyzing circle percentiles for:', selectedCircles);
      const response = await getCirclePercentileData(selectedCircles);
      console.log('Circle percentile response:', response);
      
      if (response?.successful) {
        setData({
          type: 'circles',
          data: response.result
        });
      } else {
        throw new Error(response?.errors || 'Failed to analyze circle percentiles');
      }
    } catch (err) {
      console.error('Error analyzing circle percentiles:', err);
      setError('Failed to analyze circle percentiles. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGradePercentileAnalysis = async () => {
    if (!selectedCircles.length) {
      setError('Please select at least one circle');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setData(null);
      
      console.log('Analyzing grade percentiles for:', selectedCircles);
      const response = await getGradePercentileData(selectedCircles);
      console.log('Grade percentile response:', response);
      
      if (response?.successful) {
        setData({
          type: 'grades',
          data: response.result
        });
      } else {
        throw new Error(response?.errors || 'Failed to analyze grade percentiles');
      }
    } catch (err) {
      console.error('Error analyzing grade percentiles:', err);
      setError('Failed to analyze grade percentiles. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderChart = () => {
    if (!data?.data) return null;

    const chartData = data.type === 'grades' 
      ? formatGradesData(data.data)
      : formatCirclesData(data.data);

    return (
      <div style={{ height: '400px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={THEME.grey300} />
            <XAxis 
              dataKey="name" 
              tick={{ fill: THEME.grey800 }}
              stroke={THEME.grey300}
            />
            <YAxis 
              tick={{ fill: THEME.grey800 }}
              stroke={THEME.grey300}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: THEME.grey100,
                border: `1px solid ${THEME.grey300}`,
                borderRadius: '4px'
              }}
            />
            <Legend 
              wrapperStyle={{
                paddingTop: '20px',
                color: THEME.grey800
              }}
            />
            <Bar dataKey="Basic Cash P25" fill={THEME.chart.colors[0]} />
            <Bar dataKey="Basic Cash P50" fill={THEME.chart.colors[1]} />
            <Bar dataKey="Basic Cash P75" fill={THEME.chart.colors[2]} />
            <Bar dataKey="Basic Cash Avg" fill={THEME.chart.colors[3]} strokeDasharray="3 3" />
            <Bar dataKey="TGP P25" fill={THEME.chart.colors[4]} />
            <Bar dataKey="TGP P50" fill={THEME.chart.colors[5]} />
            <Bar dataKey="TGP P75" fill={THEME.chart.colors[0]} />
            <Bar dataKey="TGP Avg" fill={THEME.chart.colors[1]} strokeDasharray="3 3" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderTable = () => {
    if (!data?.data) return null;

    return (
      <Table bordered hover responsive className="mb-0">
        <thead>
          <tr>
            <th>{data.type === 'grades' ? 'Grade' : 'Circle'}</th>
            <th colSpan={4} className="text-center">Basic Cash</th>
            <th colSpan={4} className="text-center">Guaranteed Package</th>
          </tr>
          <tr>
            <th></th>
            <th>P25</th>
            <th>P50</th>
            <th>P75</th>
            <th>Avg</th>
            <th>P25</th>
            <th>P50</th>
            <th>P75</th>
            <th>Avg</th>
          </tr>
        </thead>
        <tbody>
          {data.data.map((item, index) => (
            <tr key={index}>
              <td>{data.type === 'grades' ? item.GradeName : (availableCircles.find(c => c.id === item.CircleID)?.name || `Circle ${item.CircleID}`)}</td>
              <td>{item.P25_RCBasicCash?.toLocaleString()}</td>
              <td>{item.P50_RCBasicCash?.toLocaleString()}</td>
              <td>{item.P75_RCBasicCash?.toLocaleString()}</td>
              <td>{item.Avg_RCBasicCash?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
              <td>{item.P25_FBGuaranteedPackage?.toLocaleString()}</td>
              <td>{item.P50_FBGuaranteedPackage?.toLocaleString()}</td>
              <td>{item.P75_FBGuaranteedPackage?.toLocaleString()}</td>
              <td>{item.Avg_FBGuaranteedPackage?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  const formatGradesData = (data) => {
    if (!data || !Array.isArray(data)) {
      return [];
    }
    
    return data.map(item => ({
      name: item.GradeName,
      'Basic Cash P25': item.P25_RCBasicCash,
      'Basic Cash P50': item.P50_RCBasicCash,
      'Basic Cash P75': item.P75_RCBasicCash,
      'Basic Cash Avg': item.Avg_RCBasicCash,
      'TGP P25': item.P25_FBGuaranteedPackage,
      'TGP P50': item.P50_FBGuaranteedPackage,
      'TGP P75': item.P75_FBGuaranteedPackage,
      'TGP Avg': item.Avg_FBGuaranteedPackage,
    }));
  };

  const formatCirclesData = (data) => {
    if (!data || !Array.isArray(data)) {
      return [];
    }
    
    return data.map(item => ({
      name: availableCircles.find(c => c.id === item.CircleID)?.name || `Circle ${item.CircleID}`,
      'Basic Cash P25': item.P25_RCBasicCash,
      'Basic Cash P50': item.P50_RCBasicCash,
      'Basic Cash P75': item.P75_RCBasicCash,
      'Basic Cash Avg': item.Avg_RCBasicCash,
      'TGP P25': item.P25_FBGuaranteedPackage,
      'TGP P50': item.P50_FBGuaranteedPackage,
      'TGP P75': item.P75_FBGuaranteedPackage,
      'TGP Avg': item.Avg_FBGuaranteedPackage,
    }));
  };

  // Calculate spread/range data
  const calculateSpreadData = (dataArray) => {
    if (!dataArray || !Array.isArray(dataArray)) return [];
    
    return dataArray.map(item => {
      const basicCashRange = (item.P75_RCBasicCash || 0) - (item.P25_RCBasicCash || 0);
      const tgpRange = (item.P75_FBGuaranteedPackage || 0) - (item.P25_FBGuaranteedPackage || 0);
      const basicCashCV = item.Avg_RCBasicCash ? (basicCashRange / item.Avg_RCBasicCash) * 100 : 0;
      const tgpCV = item.Avg_FBGuaranteedPackage ? (tgpRange / item.Avg_FBGuaranteedPackage) * 100 : 0;
      
      return {
        name: item.GradeName 
          ? item.GradeName 
          : (availableCircles.find(c => c.id === item.CircleID)?.name || `Circle ${item.CircleID}`),
        'Basic Cash Range': basicCashRange,
        'Basic Cash CV %': basicCashCV,
        'TGP Range': tgpRange,
        'TGP CV %': tgpCV,
        'Basic Cash P25': item.P25_RCBasicCash,
        'Basic Cash P75': item.P75_RCBasicCash,
        'TGP P25': item.P25_FBGuaranteedPackage,
        'TGP P75': item.P75_FBGuaranteedPackage,
      };
    });
  };

  // Calculate statistical summary
  const calculateStatistics = (data) => {
    if (!data || !Array.isArray(data)) return null;
    
    const basicCashValues = data.flatMap(item => [
      item.P25_RCBasicCash,
      item.P50_RCBasicCash,
      item.P75_RCBasicCash,
      item.Avg_RCBasicCash
    ]).filter(v => v != null);
    
    const tgpValues = data.flatMap(item => [
      item.P25_FBGuaranteedPackage,
      item.P50_FBGuaranteedPackage,
      item.P75_FBGuaranteedPackage,
      item.Avg_FBGuaranteedPackage
    ]).filter(v => v != null);
    
    const calculateStats = (values) => {
      if (values.length === 0) return null;
      const sorted = [...values].sort((a, b) => a - b);
      const min = sorted[0];
      const max = sorted[sorted.length - 1];
      const mean = values.reduce((a, b) => a + b, 0) / values.length;
      const median = sorted.length % 2 === 0
        ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
        : sorted[Math.floor(sorted.length / 2)];
      const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
      const stdDev = Math.sqrt(variance);
      const cv = mean ? (stdDev / mean) * 100 : 0;
      
      return { min, max, mean, median, stdDev, cv, count: values.length };
    };
    
    return {
      basicCash: calculateStats(basicCashValues),
      tgp: calculateStats(tgpValues)
    };
  };

  // Render spread/range analysis
  const renderSpreadAnalysis = () => {
    if (!data?.data) return null;
    
    const spreadData = calculateSpreadData(data.data);
    
    return (
      <div>
        <h5 className="mb-3">Spread & Range Analysis</h5>
        <div style={{ height: '400px' }} className="mb-4">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={spreadData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={THEME.grey300} />
              <XAxis dataKey="name" tick={{ fill: THEME.grey800 }} stroke={THEME.grey300} />
              <YAxis yAxisId="left" tick={{ fill: THEME.grey800 }} stroke={THEME.grey300} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: THEME.grey800 }} stroke={THEME.grey300} />
              <Tooltip 
                contentStyle={{
                  backgroundColor: THEME.grey100,
                  border: `1px solid ${THEME.grey300}`,
                  borderRadius: '4px'
                }}
              />
              <Legend />
              <Bar yAxisId="left" dataKey="Basic Cash Range" fill={THEME.chart.colors[0]} name="Basic Cash Range (P75-P25)" />
              <Bar yAxisId="left" dataKey="TGP Range" fill={THEME.chart.colors[1]} name="TGP Range (P75-P25)" />
              <Line yAxisId="right" type="monotone" dataKey="Basic Cash CV %" stroke={THEME.chart.colors[2]} strokeWidth={2} name="Basic Cash CV %" />
              <Line yAxisId="right" type="monotone" dataKey="TGP CV %" stroke={THEME.chart.colors[3]} strokeWidth={2} name="TGP CV %" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        
        <Table bordered hover responsive>
          <thead>
            <tr>
              <th>{data.type === 'grades' ? 'Grade' : 'Circle'}</th>
              <th colSpan={2} className="text-center">Basic Cash</th>
              <th colSpan={2} className="text-center">TGP</th>
            </tr>
            <tr>
              <th></th>
              <th>Range (P75-P25)</th>
              <th>CV %</th>
              <th>Range (P75-P25)</th>
              <th>CV %</th>
            </tr>
          </thead>
          <tbody>
            {spreadData.map((item, index) => (
              <tr key={index}>
                <td>{item.name}</td>
                <td>{item['Basic Cash Range']?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                <td>{item['Basic Cash CV %']?.toFixed(2)}%</td>
                <td>{item['TGP Range']?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                <td>{item['TGP CV %']?.toFixed(2)}%</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  // Render statistical summary
  const renderStatisticalSummary = () => {
    if (!data?.data) return null;
    
    const stats = calculateStatistics(data.data);
    
    if (!stats || !stats.basicCash || !stats.tgp) {
      return <Alert variant="info">Unable to calculate statistics</Alert>;
    }
    
    return (
      <div>
        <h5 className="mb-3">Statistical Summary</h5>
        <Row className="mb-4">
          <Col md={6}>
            <Card className="mb-3">
              <Card.Header className="bg-light">
                <strong>Basic Cash Statistics</strong>
              </Card.Header>
              <Card.Body>
                <Table bordered size="sm">
                  <tbody>
                    <tr>
                      <td><strong>Minimum</strong></td>
                      <td>{stats.basicCash.min?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    </tr>
                    <tr>
                      <td><strong>Maximum</strong></td>
                      <td>{stats.basicCash.max?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    </tr>
                    <tr>
                      <td><strong>Mean</strong></td>
                      <td>{stats.basicCash.mean?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    </tr>
                    <tr>
                      <td><strong>Median</strong></td>
                      <td>{stats.basicCash.median?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    </tr>
                    <tr>
                      <td><strong>Std Deviation</strong></td>
                      <td>{stats.basicCash.stdDev?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    </tr>
                    <tr>
                      <td><strong>Coefficient of Variation</strong></td>
                      <td>{stats.basicCash.cv?.toFixed(2)}%</td>
                    </tr>
                    <tr>
                      <td><strong>Count</strong></td>
                      <td>{stats.basicCash.count}</td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="mb-3">
              <Card.Header className="bg-light">
                <strong>TGP Statistics</strong>
              </Card.Header>
              <Card.Body>
                <Table bordered size="sm">
                  <tbody>
                    <tr>
                      <td><strong>Minimum</strong></td>
                      <td>{stats.tgp.min?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    </tr>
                    <tr>
                      <td><strong>Maximum</strong></td>
                      <td>{stats.tgp.max?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    </tr>
                    <tr>
                      <td><strong>Mean</strong></td>
                      <td>{stats.tgp.mean?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    </tr>
                    <tr>
                      <td><strong>Median</strong></td>
                      <td>{stats.tgp.median?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    </tr>
                    <tr>
                      <td><strong>Std Deviation</strong></td>
                      <td>{stats.tgp.stdDev?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    </tr>
                    <tr>
                      <td><strong>Coefficient of Variation</strong></td>
                      <td>{stats.tgp.cv?.toFixed(2)}%</td>
                    </tr>
                    <tr>
                      <td><strong>Count</strong></td>
                      <td>{stats.tgp.count}</td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  // Calculate compensation ratios
  const calculateRatioData = (dataArray) => {
    if (!dataArray || !Array.isArray(dataArray)) return [];
    
    return dataArray.map(item => {
      const tgpRatio = item.Avg_RCBasicCash ? (item.Avg_FBGuaranteedPackage / item.Avg_RCBasicCash) : 0;
      const tgpP50Ratio = item.P50_RCBasicCash ? (item.P50_FBGuaranteedPackage / item.P50_RCBasicCash) : 0;
      const premium = item.Avg_RCBasicCash ? ((item.Avg_FBGuaranteedPackage - item.Avg_RCBasicCash) / item.Avg_RCBasicCash) * 100 : 0;
      
      return {
        name: item.GradeName 
          ? item.GradeName 
          : (availableCircles.find(c => c.id === item.CircleID)?.name || `Circle ${item.CircleID}`),
        'TGP Ratio': tgpRatio,
        'TGP P50 Ratio': tgpP50Ratio,
        'Premium %': premium,
        'Basic Cash Avg': item.Avg_RCBasicCash,
        'TGP Avg': item.Avg_FBGuaranteedPackage,
        'Basic Cash P50': item.P50_RCBasicCash,
        'TGP P50': item.P50_FBGuaranteedPackage,
      };
    });
  };

  // Calculate key insights
  const calculateKeyInsights = (dataArray, dataType) => {
    if (!dataArray || !Array.isArray(dataArray) || dataArray.length === 0) return [];
    
    const insights = [];
    
    // Calculate overall statistics
    const allBasicCash = dataArray.map(item => item.Avg_RCBasicCash).filter(v => v != null);
    const allTGP = dataArray.map(item => item.Avg_FBGuaranteedPackage).filter(v => v != null);
    const overallBasicCashAvg = allBasicCash.reduce((a, b) => a + b, 0) / allBasicCash.length;
    const overallTGPAvg = allTGP.reduce((a, b) => a + b, 0) / allTGP.length;
    
    // Find highest and lowest
    const highestBasicCash = dataArray.reduce((max, item) => 
      (item.Avg_RCBasicCash || 0) > (max.Avg_RCBasicCash || 0) ? item : max, dataArray[0]);
    const lowestBasicCash = dataArray.reduce((min, item) => 
      (item.Avg_RCBasicCash || Infinity) < (min.Avg_RCBasicCash || Infinity) ? item : min, dataArray[0]);
    const highestTGP = dataArray.reduce((max, item) => 
      (item.Avg_FBGuaranteedPackage || 0) > (max.Avg_FBGuaranteedPackage || 0) ? item : max, dataArray[0]);
    
    // Find highest variability (CV)
    const spreadData = calculateSpreadData(dataArray);
    const highestVariability = spreadData.reduce((max, item) => 
      (item['TGP CV %'] || 0) > (max['TGP CV %'] || 0) ? item : max, spreadData[0]);
    
    // Find highest premium
    const ratioData = calculateRatioData(dataArray);
    const highestPremium = ratioData.reduce((max, item) => 
      (item['Premium %'] || 0) > (max['Premium %'] || 0) ? item : max, ratioData[0]);
    
    // Insight 1: Highest compensation
    const highestName = dataType === 'grades' 
      ? highestTGP.GradeName 
      : (availableCircles.find(c => c.id === highestTGP.CircleID)?.name || `Circle ${highestTGP.CircleID}`);
    insights.push({
      type: 'info',
      title: 'Highest Total Compensation',
      message: `${highestName} has the highest TGP at ${highestTGP.Avg_FBGuaranteedPackage?.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
      value: highestTGP.Avg_FBGuaranteedPackage
    });
    
    // Insight 2: Lowest compensation
    const lowestName = dataType === 'grades' 
      ? lowestBasicCash.GradeName 
      : (availableCircles.find(c => c.id === lowestBasicCash.CircleID)?.name || `Circle ${lowestBasicCash.CircleID}`);
    insights.push({
      type: 'warning',
      title: 'Lowest Basic Cash',
      message: `${lowestName} has the lowest basic cash at ${lowestBasicCash.Avg_RCBasicCash?.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
      value: lowestBasicCash.Avg_RCBasicCash
    });
    
    // Insight 3: Highest variability
    if (highestVariability && highestVariability['TGP CV %'] > 30) {
      insights.push({
        type: 'warning',
        title: 'High Variability Detected',
        message: `${highestVariability.name} shows high compensation variability (CV: ${highestVariability['TGP CV %']?.toFixed(2)}%). Consider reviewing compensation structure.`,
        value: highestVariability['TGP CV %']
      });
    }
    
    // Insight 4: Highest premium
    if (highestPremium && highestPremium['Premium %'] > 20) {
      insights.push({
        type: 'info',
        title: 'High Compensation Premium',
        message: `${highestPremium.name} shows a ${highestPremium['Premium %']?.toFixed(1)}% premium over basic cash, indicating significant additional benefits.`,
        value: highestPremium['Premium %']
      });
    }
    
    // Insight 5: Compensation spread
    const spread = overallTGPAvg - overallBasicCashAvg;
    const spreadPercent = (spread / overallBasicCashAvg) * 100;
    insights.push({
      type: 'info',
      title: 'Overall Compensation Spread',
      message: `Average TGP is ${spreadPercent?.toFixed(1)}% higher than basic cash, with an average spread of ${spread?.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
      value: spreadPercent
    });
    
    return insights;
  };

  // Render key insights
  const renderKeyInsights = () => {
    if (!data?.data) return null;
    
    const insights = calculateKeyInsights(data.data, data.type);
    
    if (insights.length === 0) {
      return <Alert variant="info">No insights available for this data</Alert>;
    }
    
    return (
      <div>
        <h5 className="mb-3">Key Insights & Recommendations</h5>
        <Row>
          {insights.map((insight, index) => (
            <Col md={6} key={index} className="mb-3">
              <Card className={`h-100 border-${insight.type === 'warning' ? 'warning' : 'info'}`}>
                <Card.Header className={`bg-${insight.type === 'warning' ? 'warning' : 'info'} text-white`}>
                  <strong>{insight.title}</strong>
                </Card.Header>
                <Card.Body>
                  <p className="mb-0">{insight.message}</p>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  // Render compensation ratio analysis
  const renderRatioAnalysis = () => {
    if (!data?.data) return null;
    
    const ratioData = calculateRatioData(data.data);
    
    return (
      <div>
        <h5 className="mb-3">Compensation Ratio Analysis</h5>
        <p className="text-muted mb-3">
          This analysis shows the ratio between Total Guaranteed Package (TGP) and Basic Cash, 
          indicating the premium provided through additional benefits and guarantees.
        </p>
        <div style={{ height: '400px' }} className="mb-4">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={ratioData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={THEME.grey300} />
              <XAxis dataKey="name" tick={{ fill: THEME.grey800 }} stroke={THEME.grey300} angle={-45} textAnchor="end" height={100} />
              <YAxis yAxisId="left" tick={{ fill: THEME.grey800 }} stroke={THEME.grey300} label={{ value: 'Ratio', angle: -90, position: 'insideLeft' }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: THEME.grey800 }} stroke={THEME.grey300} label={{ value: 'Premium %', angle: 90, position: 'insideRight' }} />
              <Tooltip 
                contentStyle={{
                  backgroundColor: THEME.grey100,
                  border: `1px solid ${THEME.grey300}`,
                  borderRadius: '4px'
                }}
              />
              <Legend />
              <Bar yAxisId="left" dataKey="TGP Ratio" fill={THEME.chart.colors[0]} name="TGP / Basic Cash Ratio" />
              <Line yAxisId="right" type="monotone" dataKey="Premium %" stroke={THEME.chart.colors[2]} strokeWidth={2} name="Premium %" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        
        <Table bordered hover responsive>
          <thead>
            <tr>
              <th>{data.type === 'grades' ? 'Grade' : 'Circle'}</th>
              <th>Basic Cash Avg</th>
              <th>TGP Avg</th>
              <th>TGP Ratio</th>
              <th>Premium %</th>
            </tr>
          </thead>
          <tbody>
            {ratioData.map((item, index) => (
              <tr key={index}>
                <td><strong>{item.name}</strong></td>
                <td>{item['Basic Cash Avg']?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                <td>{item['TGP Avg']?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                <td>{item['TGP Ratio']?.toFixed(2)}x</td>
                <td>
                  <Badge bg={item['Premium %'] > 20 ? 'success' : item['Premium %'] > 10 ? 'info' : 'secondary'}>
                    {item['Premium %']?.toFixed(1)}%
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  // Render market positioning
  const renderMarketPositioning = () => {
    if (!data?.data) return null;
    
    const positioningData = data.data.map(item => {
      const name = item.GradeName 
        ? item.GradeName 
        : (availableCircles.find(c => c.id === item.CircleID)?.name || `Circle ${item.CircleID}`);
      
      // Calculate position relative to median
      const medianBasicCash = data.data.map(i => i.P50_RCBasicCash).filter(v => v != null).sort((a, b) => a - b);
      const medianTGP = data.data.map(i => i.P50_FBGuaranteedPackage).filter(v => v != null).sort((a, b) => a - b);
      const overallMedianBasicCash = medianBasicCash[Math.floor(medianBasicCash.length / 2)];
      const overallMedianTGP = medianTGP[Math.floor(medianTGP.length / 2)];
      
      const basicCashPosition = item.P50_RCBasicCash ? ((item.P50_RCBasicCash / overallMedianBasicCash) - 1) * 100 : 0;
      const tgpPosition = item.P50_FBGuaranteedPackage ? ((item.P50_FBGuaranteedPackage / overallMedianTGP) - 1) * 100 : 0;
      
      return {
        name,
        'Basic Cash Position': basicCashPosition,
        'TGP Position': tgpPosition,
        'Basic Cash P50': item.P50_RCBasicCash,
        'TGP P50': item.P50_FBGuaranteedPackage,
      };
    });
    
    return (
      <div>
        <h5 className="mb-3">Market Positioning Analysis</h5>
        <p className="text-muted mb-3">
          Shows relative positioning compared to the overall median. Positive values indicate above-median compensation.
        </p>
        <div style={{ height: '400px' }} className="mb-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={positioningData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={THEME.grey300} />
              <XAxis dataKey="name" tick={{ fill: THEME.grey800 }} stroke={THEME.grey300} angle={-45} textAnchor="end" height={100} />
              <YAxis tick={{ fill: THEME.grey800 }} stroke={THEME.grey300} label={{ value: 'Position vs Median (%)', angle: -90, position: 'insideLeft' }} />
              <Tooltip 
                contentStyle={{
                  backgroundColor: THEME.grey100,
                  border: `1px solid ${THEME.grey300}`,
                  borderRadius: '4px'
                }}
              />
              <Legend />
              <Bar dataKey="Basic Cash Position" fill={THEME.chart.colors[0]} name="Basic Cash vs Median %" />
              <Bar dataKey="TGP Position" fill={THEME.chart.colors[1]} name="TGP vs Median %" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        <Table bordered hover responsive>
          <thead>
            <tr>
              <th>{data.type === 'grades' ? 'Grade' : 'Circle'}</th>
              <th>Basic Cash P50</th>
              <th>Position vs Median</th>
              <th>TGP P50</th>
              <th>Position vs Median</th>
            </tr>
          </thead>
          <tbody>
            {positioningData.map((item, index) => (
              <tr key={index}>
                <td><strong>{item.name}</strong></td>
                <td>{item['Basic Cash P50']?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                <td>
                  <Badge bg={item['Basic Cash Position'] > 10 ? 'success' : item['Basic Cash Position'] > 0 ? 'info' : item['Basic Cash Position'] < -10 ? 'danger' : 'secondary'}>
                    {item['Basic Cash Position'] > 0 ? '+' : ''}{item['Basic Cash Position']?.toFixed(1)}%
                  </Badge>
                </td>
                <td>{item['TGP P50']?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                <td>
                  <Badge bg={item['TGP Position'] > 10 ? 'success' : item['TGP Position'] > 0 ? 'info' : item['TGP Position'] < -10 ? 'danger' : 'secondary'}>
                    {item['TGP Position'] > 0 ? '+' : ''}{item['TGP Position']?.toFixed(1)}%
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  // Render comparison dashboard
  const renderComparisonDashboard = () => {
    if (!data?.data || data.type !== 'circles') return null;
    
    const circleData = data.data;
    
    return (
      <div>
        <h5 className="mb-3">Circle Comparison Dashboard</h5>
        <Row>
          {circleData.slice(0, 4).map((item, index) => {
            const circleName = availableCircles.find(c => c.id === item.CircleID)?.name || `Circle ${item.CircleID}`;
            return (
              <Col md={6} lg={3} key={index} className="mb-3">
                <Card className="h-100">
                  <Card.Header className="bg-primary text-white">
                    <strong>{circleName}</strong>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-2">
                      <small className="text-muted">Basic Cash</small>
                      <div>
                        <strong>P50:</strong> {item.P50_RCBasicCash?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </div>
                      <div>
                        <strong>Avg:</strong> {item.Avg_RCBasicCash?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </div>
                      <div>
                        <small>Range: {item.P25_RCBasicCash?.toLocaleString(undefined, { maximumFractionDigits: 0 })} - {item.P75_RCBasicCash?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</small>
                      </div>
                    </div>
                    <hr />
                    <div>
                      <small className="text-muted">TGP</small>
                      <div>
                        <strong>P50:</strong> {item.P50_FBGuaranteedPackage?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </div>
                      <div>
                        <strong>Avg:</strong> {item.Avg_FBGuaranteedPackage?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </div>
                      <div>
                        <small>Range: {item.P25_FBGuaranteedPackage?.toLocaleString(undefined, { maximumFractionDigits: 0 })} - {item.P75_FBGuaranteedPackage?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</small>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
        {circleData.length > 4 && (
          <Alert variant="info" className="mt-3">
            Showing first 4 circles. Select fewer circles for detailed comparison.
          </Alert>
        )}
      </div>
    );
  };

  return (
    <Card className="shadow-sm">
      <Card.Header className="bg-white">
        <Row className="align-items-center">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Select Circles</Form.Label>
              <Form.Select 
                value="" 
                onChange={handleCircleSelect}
                disabled={loading}
              >
                <option value="">Choose a circle...</option>
                {availableCircles.map(circle => (
                  <option key={circle.id} value={circle.id}>
                    {circle.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            {selectedCircles.length > 0 && (
              <div className="mt-2">
                {selectedCircles.map(circleId => {
                  const circle = availableCircles.find(c => c.id === Number(circleId));
                  return (
                    <Badge 
                      key={circleId}
                      bg="light"
                      text="dark"
                      className="me-2 mb-2"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleRemoveCircle(circleId)}
                    >
                      {circle?.name || `Circle ${circleId}`} ×
                    </Badge>
                  );
                })}
              </div>
            )}
          </Col>
          <Col md={6} className="text-end">
            <Button
              variant="success"
              onClick={handleCirclePercentileAnalysis}
              disabled={loading || selectedCircles.length === 0}
              className="me-2"
            >
              {loading ? 'Analyzing...' : 'Analyze Circle Percentiles'}
            </Button>
            <Button
              variant="success"
              onClick={handleGradePercentileAnalysis}
              disabled={loading || selectedCircles.length === 0}
            >
              {loading ? 'Analyzing...' : 'Analyze Grade Percentiles'}
            </Button>
          </Col>
        </Row>
      </Card.Header>

      <Card.Body>
        {error ? (
          <Alert variant="danger">{error}</Alert>
        ) : loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-3 text-muted">Analyzing data...</p>
          </div>
        ) : data ? (
          <>
            <Tabs defaultActiveKey="insights" className="mb-4">
              <Tab eventKey="insights" title="Key Insights">
                {renderKeyInsights()}
              </Tab>
              <Tab eventKey="visualization" title="Percentiles">
                {renderChart()}
              </Tab>
              <Tab eventKey="tables" title="Data Table">
                {renderTable()}
              </Tab>
              <Tab eventKey="spread" title="Spread Analysis">
                {renderSpreadAnalysis()}
              </Tab>
              <Tab eventKey="ratios" title="Ratio Analysis">
                {renderRatioAnalysis()}
              </Tab>
              <Tab eventKey="positioning" title="Market Positioning">
                {renderMarketPositioning()}
              </Tab>
              <Tab eventKey="statistics" title="Statistics">
                {renderStatisticalSummary()}
              </Tab>
              {data.type === 'circles' && (
                <Tab eventKey="comparison" title="Comparison">
                  {renderComparisonDashboard()}
                </Tab>
              )}
            </Tabs>
          </>
        ) : (
          <Alert variant="info">
            Select circles and click one of the analysis buttons to view data
          </Alert>
        )}
      </Card.Body>
    </Card>
  );
};

export default DataAnalysis; 